import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  getAdminStats, getAdminUsers, getPlaylists, createPlaylist, syncPlaylist, 
  deletePlaylist, getIPTVSources, updateUserCredits, updateUserSubscription,
  updateAdminUser, deleteAdminUser, syncEPG, createAdminUser,
  getScannerStatus, triggerScan, getScannerSources, addCustomSource, removeCustomSource, getScannerLogs,
  importXtream, getXtreamConnections, deleteXtreamConnection, updateXtreamConnection
} from '../services/api';
import { toast } from 'sonner';
import { 
  Users, Tv2, ListVideo, CreditCard, Settings, RefreshCw, Plus, Trash2,
  Download, ExternalLink, Search, MoreVertical, Crown, Ban, Check,
  DollarSign, Calendar, Shield, Database, Play, UserPlus, Radar, Clock, Wifi, WifiOff, Bot, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import AIAdminChat from '../components/AIAdminChat';

export default function AdminPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [iptvSources, setIptvSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [showAIChat, setShowAIChat] = useState(false);

  // Form states
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistUrl, setNewPlaylistUrl] = useState('');
  const [selectedSource, setSelectedSource] = useState(null);
  const [epgUrl, setEpgUrl] = useState('');

  // User management
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [subscriptionType, setSubscriptionType] = useState('');
  const [subscriptionDays, setSubscriptionDays] = useState('30');
  
  // Add User form states
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    username: '',
    password: '',
    role: 'user',
    credits: 0,
    subscription_type: 'none',
    subscription_days: 0,
    max_devices: 1
  });

  // Scanner states
  const [scannerStatus, setScannerStatus] = useState(null);
  const [scannerSources, setScannerSources] = useState(null);
  const [scannerLogs, setScannerLogs] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [newSourceType, setNewSourceType] = useState('live');

  // Xtream Codes states
  const [xtreamConnections, setXtreamConnections] = useState([]);
  const [xtreamForm, setXtreamForm] = useState({ server_url: 'http://proxpanel.fans', username: '', password: '', import_live: true, import_vod: true });
  const [xtreamImporting, setXtreamImporting] = useState(false);
  const [editingConn, setEditingConn] = useState(null); // { server_url, username, password }

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    try {
      const [statsData, usersData, playlistsData, sourcesData] = await Promise.all([
        getAdminStats(),
        getAdminUsers(),
        getPlaylists(),
        getIPTVSources()
      ]);
      setStats(statsData);
      setUsers(usersData.users);
      setPlaylists(playlistsData);
      setIptvSources(sourcesData);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlaylist = async () => {
    if (!newPlaylistName || !newPlaylistUrl) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const playlist = await createPlaylist(newPlaylistName, newPlaylistUrl);
      setPlaylists(prev => [...prev, playlist]);
      setNewPlaylistName('');
      setNewPlaylistUrl('');
      toast.success('Playlist added successfully');
    } catch (error) {
      toast.error('Failed to add playlist');
    }
  };

  const handleSyncPlaylist = async (playlistId) => {
    setSyncing(prev => ({ ...prev, [playlistId]: true }));
    try {
      const result = await syncPlaylist(playlistId);
      toast.success(`Synced ${result.channel_count} channels`);
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync playlist');
    } finally {
      setSyncing(prev => ({ ...prev, [playlistId]: false }));
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    try {
      await deletePlaylist(playlistId);
      setPlaylists(prev => prev.filter(p => p.id !== playlistId));
      toast.success('Playlist deleted');
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  const handleAddFromSource = (source) => {
    setNewPlaylistName(source.name);
    setNewPlaylistUrl(source.url);
    setSelectedSource(null);
  };

  const handleSyncEPG = async () => {
    if (!epgUrl) {
      toast.error('Please enter an EPG URL');
      return;
    }

    try {
      const result = await syncEPG(epgUrl);
      toast.success(result.message);
    } catch (error) {
      toast.error('Failed to sync EPG');
    }
  };

  const handleUpdateCredits = async () => {
    if (!selectedUser || !creditsAmount) return;

    try {
      await updateUserCredits(selectedUser.id, parseFloat(creditsAmount), 'add');
      toast.success(`Added ${creditsAmount} credits to ${selectedUser.username}`);
      fetchData();
      setSelectedUser(null);
      setCreditsAmount('');
    } catch (error) {
      toast.error('Failed to update credits');
    }
  };

  const handleUpdateSubscription = async () => {
    if (!selectedUser || !subscriptionType) return;

    try {
      await updateUserSubscription(
        selectedUser.id, 
        subscriptionType, 
        subscriptionType !== 'lifetime' ? parseInt(subscriptionDays) : null
      );
      toast.success(`Updated subscription for ${selectedUser.username}`);
      fetchData();
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleToggleUserStatus = async (user) => {
    try {
      await updateAdminUser(user.id, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'disabled' : 'enabled'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteAdminUser(userId);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.username || !newUser.password) {
      toast.error('Please fill in email, username and password');
      return;
    }

    try {
      await createAdminUser(newUser);
      toast.success(`User ${newUser.username} created successfully`);
      setShowAddUser(false);
      setNewUser({
        email: '',
        username: '',
        password: '',
        role: 'user',
        credits: 0,
        subscription_type: 'none',
        subscription_days: 0,
        max_devices: 1
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Scanner functions
  const fetchScannerData = async () => {
    try {
      const [status, sources, logs] = await Promise.all([
        getScannerStatus(),
        getScannerSources(),
        getScannerLogs(10)
      ]);
      setScannerStatus(status);
      setScannerSources(sources);
      setScannerLogs(logs.logs || []);
    } catch (error) {
      console.error('Failed to load scanner data:', error);
    }
  };

  const handleTriggerScan = async () => {
    setIsScanning(true);
    try {
      await triggerScan();
      toast.success('Scanner triggered! Scanning all sources...');
      // Refresh status after a delay
      setTimeout(fetchScannerData, 5000);
    } catch (error) {
      toast.error('Failed to trigger scan');
    } finally {
      setIsScanning(false);
    }
  };

  const handleAddCustomSource = async () => {
    if (!newSourceName || !newSourceUrl) {
      toast.error('Please fill in name and URL');
      return;
    }
    try {
      await addCustomSource(newSourceName, newSourceUrl, newSourceType);
      toast.success('Custom source added!');
      setNewSourceName('');
      setNewSourceUrl('');
      fetchScannerData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add source');
    }
  };

  const handleRemoveCustomSource = async (sourceId) => {
    try {
      await removeCustomSource(sourceId);
      toast.success('Source removed');
      fetchScannerData();
    } catch (error) {
      toast.error('Failed to remove source');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-loading">
        <h1 className="text-3xl font-bold">Admin Panel</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 rounded-xl skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Admin Panel</h1>
          <p className="text-white/60 mt-1">Manage your IPTV platform</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => setShowAIChat(true)} 
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
          >
            <span className="mr-2">🤖</span>
            Sabooba
          </Button>
          <Button onClick={fetchData} variant="outline" className="rounded-full">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* AI Admin Chat */}
      <AIAdminChat isOpen={showAIChat} onClose={() => setShowAIChat(false)} />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={stats?.total_users || 0}
          sublabel={`${stats?.active_users || 0} active`}
          color="primary"
        />
        <StatCard
          icon={Tv2}
          label="Channels"
          value={stats?.total_channels || 0}
          sublabel={`${stats?.total_playlists || 0} playlists`}
          color="secondary"
        />
        <StatCard
          icon={ListVideo}
          label="Categories"
          value={stats?.total_categories || 0}
          color="accent"
        />
        <StatCard
          icon={Crown}
          label="Lifetime Users"
          value={stats?.subscriptions?.lifetime || 0}
          sublabel="Premium"
          color="warning"
        />
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="playlists" className="space-y-6">
        <TabsList className="bg-[rgb(15,15,16)] border border-white/10">
          <TabsTrigger value="playlists" className="data-[state=active]:bg-[rgb(124,58,237)]">
            <ListVideo className="w-4 h-4 mr-2" />
            Playlists
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-[rgb(124,58,237)]">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="scanner" className="data-[state=active]:bg-[rgb(124,58,237)]" onClick={fetchScannerData}>
            <Radar className="w-4 h-4 mr-2" />
            Auto Scanner
          </TabsTrigger>
          <TabsTrigger value="sources" className="data-[state=active]:bg-[rgb(124,58,237)]">
            <Database className="w-4 h-4 mr-2" />
            IPTV Sources
          </TabsTrigger>
          <TabsTrigger value="epg" className="data-[state=active]:bg-[rgb(124,58,237)]">
            <Calendar className="w-4 h-4 mr-2" />
            EPG
          </TabsTrigger>
          <TabsTrigger value="xtream" className="data-[state=active]:bg-[rgb(124,58,237)]" onClick={async () => { try { const c = await getXtreamConnections(); setXtreamConnections(c); } catch(e){} }}>
            <Wifi className="w-4 h-4 mr-2" />
            Xtream Codes
          </TabsTrigger>
        </TabsList>

        {/* Playlists Tab */}
        <TabsContent value="playlists" className="space-y-6">
          {/* Add Playlist Form */}
          <div className="glass-card rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Plus className="w-5 h-5 text-[rgb(124,58,237)]" />
              Add Playlist
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Playlist Name</Label>
                <Input
                  placeholder="e.g., Sports Channels"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  className="bg-[rgb(31,31,34)] border-white/10"
                  data-testid="playlist-name-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>M3U URL</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://example.com/playlist.m3u"
                    value={newPlaylistUrl}
                    onChange={(e) => setNewPlaylistUrl(e.target.value)}
                    className="bg-[rgb(31,31,34)] border-white/10 flex-1"
                    data-testid="playlist-url-input"
                  />
                  <Button onClick={handleAddPlaylist} className="rounded-full bg-[rgb(124,58,237)]" data-testid="add-playlist-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Playlists Table */}
          <div className="glass-card rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Channels</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {playlists.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-white/40">
                      No playlists added yet. Add one above or select from IPTV Sources.
                    </TableCell>
                  </TableRow>
                ) : (
                  playlists.map((playlist) => (
                    <TableRow key={playlist.id} className="border-white/10" data-testid={`playlist-row-${playlist.id}`}>
                      <TableCell className="font-medium">{playlist.name}</TableCell>
                      <TableCell>{playlist.channel_count}</TableCell>
                      <TableCell className="text-white/60">
                        {new Date(playlist.last_updated).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          playlist.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {playlist.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSyncPlaylist(playlist.id)}
                            disabled={syncing[playlist.id]}
                            className="rounded-full"
                            data-testid={`sync-playlist-${playlist.id}`}
                          >
                            <RefreshCw className={`w-4 h-4 mr-1 ${syncing[playlist.id] ? 'animate-spin' : ''}`} />
                            Sync
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeletePlaylist(playlist.id)}
                            className="rounded-full"
                            data-testid={`delete-playlist-${playlist.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          {/* Add User Button */}
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">User Management</h2>
            <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
              <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700" data-testid="add-user-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[rgb(15,15,16)] border-white/10" data-testid="add-user-dialog">
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                  <DialogDescription>Create a new user account</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email *</Label>
                      <Input 
                        type="email"
                        placeholder="user@example.com"
                        value={newUser.email}
                        onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                    <div>
                      <Label>Username *</Label>
                      <Input 
                        placeholder="username"
                        value={newUser.username}
                        onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Password *</Label>
                      <Input 
                        type="password"
                        placeholder="••••••••"
                        value={newUser.password}
                        onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                        <SelectTrigger className="bg-white/5 border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[rgb(15,15,16)] border-white/10">
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Subscription Type</Label>
                      <Select value={newUser.subscription_type} onValueChange={(v) => setNewUser(prev => ({ ...prev, subscription_type: v }))}>
                        <SelectTrigger className="bg-white/5 border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[rgb(15,15,16)] border-white/10">
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="yearly">Yearly</SelectItem>
                          <SelectItem value="lifetime">Lifetime</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Subscription Days</Label>
                      <Input 
                        type="number"
                        placeholder="30"
                        value={newUser.subscription_days}
                        onChange={(e) => setNewUser(prev => ({ ...prev, subscription_days: parseInt(e.target.value) || 0 }))}
                        className="bg-white/5 border-white/10"
                        disabled={newUser.subscription_type === 'lifetime' || newUser.subscription_type === 'none'}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Starting Credits</Label>
                      <Input 
                        type="number"
                        placeholder="0"
                        value={newUser.credits}
                        onChange={(e) => setNewUser(prev => ({ ...prev, credits: parseFloat(e.target.value) || 0 }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                    <div>
                      <Label>Max Devices</Label>
                      <Input 
                        type="number"
                        placeholder="1"
                        value={newUser.max_devices}
                        onChange={(e) => setNewUser(prev => ({ ...prev, max_devices: parseInt(e.target.value) || 1 }))}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
                  <Button onClick={handleAddUser} className="bg-green-600 hover:bg-green-700">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="glass-card rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-white/10" data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{user.username}</p>
                        <p className="text-xs text-white/40">{user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.role === 'admin' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                      }`}>
                        {user.role === 'admin' && <Crown className="w-3 h-3 inline mr-1" />}
                        {user.role}
                      </span>
                    </TableCell>
                    <TableCell className="capitalize">{user.subscription_type}</TableCell>
                    <TableCell>{user.credits?.toFixed(2)}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {user.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost" className="rounded-full">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[rgb(15,15,16)] border-white/10">
                          <Dialog>
                            <DialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSelectedUser(user); }}>
                                <DollarSign className="w-4 h-4 mr-2" />
                                Add Credits
                              </DropdownMenuItem>
                            </DialogTrigger>
                          </Dialog>
                          <Dialog>
                            <DialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSelectedUser(user); }}>
                                <Crown className="w-4 h-4 mr-2" />
                                Set Subscription
                              </DropdownMenuItem>
                            </DialogTrigger>
                          </Dialog>
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem onClick={() => handleToggleUserStatus(user)}>
                            {user.is_active ? (
                              <><Ban className="w-4 h-4 mr-2" /> Disable</>
                            ) : (
                              <><Check className="w-4 h-4 mr-2" /> Enable</>
                            )}
                          </DropdownMenuItem>
                          {user.role !== 'admin' && (
                            <DropdownMenuItem 
                              className="text-red-400"
                              onClick={() => handleDeleteUser(user.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Credits Dialog */}
          <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
            <DialogContent className="bg-[rgb(15,15,16)] border-white/10">
              <DialogHeader>
                <DialogTitle>Manage User: {selectedUser?.username}</DialogTitle>
                <DialogDescription className="text-white/60">
                  Add credits or update subscription for this user
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Add Credits */}
                <div className="space-y-3">
                  <Label>Add Credits</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={creditsAmount}
                      onChange={(e) => setCreditsAmount(e.target.value)}
                      className="bg-[rgb(31,31,34)] border-white/10"
                    />
                    <Button onClick={handleUpdateCredits} className="rounded-full bg-[rgb(16,185,129)]">
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  <p className="text-xs text-white/40">Current: {selectedUser?.credits?.toFixed(2)} credits</p>
                </div>

                {/* Update Subscription */}
                <div className="space-y-3">
                  <Label>Subscription</Label>
                  <div className="flex gap-2">
                    <Select value={subscriptionType} onValueChange={setSubscriptionType}>
                      <SelectTrigger className="bg-[rgb(31,31,34)] border-white/10">
                        <SelectValue placeholder="Select plan" />
                      </SelectTrigger>
                      <SelectContent className="bg-[rgb(15,15,16)] border-white/10">
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="credits">Credits Only</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                        <SelectItem value="lifetime">Lifetime</SelectItem>
                      </SelectContent>
                    </Select>
                    {subscriptionType && subscriptionType !== 'lifetime' && subscriptionType !== 'none' && (
                      <Input
                        type="number"
                        placeholder="Days"
                        value={subscriptionDays}
                        onChange={(e) => setSubscriptionDays(e.target.value)}
                        className="bg-[rgb(31,31,34)] border-white/10 w-24"
                      />
                    )}
                    <Button onClick={handleUpdateSubscription} className="rounded-full bg-[rgb(124,58,237)]">
                      Update
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* IPTV Sources Tab */}
        <TabsContent value="sources" className="space-y-6">
          <div className="glass-card rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-[rgb(16,185,129)]" />
              Free IPTV Playlist Sources
            </h3>
            <p className="text-white/60 text-sm mb-6">
              These are publicly available, free-to-air IPTV playlists. Click "Add" to import them into your platform.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              {iptvSources.map((source, index) => (
                <div
                  key={index}
                  className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[rgb(124,58,237)]/50 transition-all"
                  data-testid={`iptv-source-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium">{source.name}</h4>
                      <p className="text-sm text-white/40 mt-1">{source.description}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddFromSource(source)}
                      className="rounded-full bg-[rgb(124,58,237)] ml-4"
                      data-testid={`add-source-${index}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[rgb(124,58,237)] hover:underline mt-2 inline-flex items-center gap-1"
                  >
                    View URL <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* EPG Tab */}
        <TabsContent value="epg" className="space-y-6">
          <div className="glass-card rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[rgb(59,130,246)]" />
              Sync EPG (Electronic Program Guide)
            </h3>
            <p className="text-white/60 text-sm">
              Import program schedule data from an EPG XML URL. This provides TV listings for your channels.
            </p>
            
            <div className="flex gap-2">
              <Input
                placeholder="https://example.com/epg.xml"
                value={epgUrl}
                onChange={(e) => setEpgUrl(e.target.value)}
                className="bg-[rgb(31,31,34)] border-white/10 flex-1"
                data-testid="epg-url-input"
              />
              <Button onClick={handleSyncEPG} className="rounded-full bg-[rgb(59,130,246)]" data-testid="sync-epg-btn">
                <Download className="w-4 h-4 mr-2" />
                Sync EPG
              </Button>
            </div>

            <div className="p-4 rounded-lg bg-white/5 mt-4">
              <h4 className="font-medium mb-2">Popular EPG Sources:</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li>• IPTV-Org EPG: https://iptv-org.github.io/epg/guides/us/tvguide.com.xml</li>
                <li>• European EPG: https://iptv-org.github.io/epg/guides/uk/bt.com.xml</li>
                <li>• Search for more at: https://github.com/iptv-org/epg</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        {/* Scanner Tab */}
        <TabsContent value="scanner" className="space-y-6">
          <ScannerTabContent 
            scannerStatus={scannerStatus}
            scannerSources={scannerSources}
            scannerLogs={scannerLogs}
            isScanning={isScanning}
            newSourceName={newSourceName}
            newSourceUrl={newSourceUrl}
            newSourceType={newSourceType}
            setNewSourceName={setNewSourceName}
            setNewSourceUrl={setNewSourceUrl}
            setNewSourceType={setNewSourceType}
            handleTriggerScan={handleTriggerScan}
            handleAddCustomSource={handleAddCustomSource}
            handleRemoveCustomSource={handleRemoveCustomSource}
          />
        </TabsContent>

        {/* Xtream Codes Tab */}
        <TabsContent value="xtream" className="space-y-6">
          {/* Import Form */}
          <div className="glass-card rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Wifi className="w-5 h-5 text-[rgb(124,58,237)]" />
              Connect Xtream Codes Panel
            </h3>
            <p className="text-sm text-white/60">Import live TV, VOD, and series from any Xtream Codes compatible panel.</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Server URL</Label>
                <Input
                  data-testid="xtream-server-url"
                  placeholder="http://yourpanel.com:8080"
                  value={xtreamForm.server_url}
                  onChange={e => setXtreamForm(f => ({ ...f, server_url: e.target.value }))}
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  data-testid="xtream-username"
                  placeholder="your_username"
                  value={xtreamForm.username}
                  onChange={e => setXtreamForm(f => ({ ...f, username: e.target.value }))}
                  className="bg-white/5 border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  data-testid="xtream-password"
                  type="password"
                  placeholder="your_password"
                  value={xtreamForm.password}
                  onChange={e => setXtreamForm(f => ({ ...f, password: e.target.value }))}
                  className="bg-white/5 border-white/10"
                />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={xtreamForm.import_live} onChange={e => setXtreamForm(f => ({ ...f, import_live: e.target.checked }))} className="rounded" />
                <span className="text-sm">Import Live TV</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={xtreamForm.import_vod} onChange={e => setXtreamForm(f => ({ ...f, import_vod: e.target.checked }))} className="rounded" />
                <span className="text-sm">Import VOD Movies</span>
              </label>
            </div>
            <Button
              data-testid="xtream-import-btn"
              onClick={async () => {
                if (!xtreamForm.server_url || !xtreamForm.username || !xtreamForm.password) {
                  toast.error('Fill in all fields');
                  return;
                }
                setXtreamImporting(true);
                try {
                  const res = await importXtream(xtreamForm.server_url, xtreamForm.username, xtreamForm.password, xtreamForm.import_live, xtreamForm.import_vod);
                  toast.success(res.message || 'Import started in background');
                  setTimeout(async () => {
                    const c = await getXtreamConnections();
                    setXtreamConnections(c);
                  }, 3000);
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Import failed');
                } finally {
                  setXtreamImporting(false);
                }
              }}
              disabled={xtreamImporting}
              className="bg-[rgb(124,58,237)] hover:bg-[rgb(109,40,217)]"
            >
              {xtreamImporting ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Connecting...</> : <><Download className="w-4 h-4 mr-2" />Import Channels</>}
            </Button>
          </div>

          {/* Existing Connections */}
          {xtreamConnections.length > 0 && (
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">Connected Panels ({xtreamConnections.length})</h3>
              <div className="space-y-3">
                {xtreamConnections.map((conn, i) => (
                  <div key={i} className="border border-white/10 rounded-lg overflow-hidden">
                    {editingConn?.server_url === conn.server_url ? (
                      /* Inline Edit Form */
                      <div className="p-4 bg-white/5 space-y-3">
                        <div className="grid md:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs mb-1">Server URL</Label>
                            <Input value={editingConn.server_url} disabled className="bg-white/5 border-white/10 text-white/50 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs mb-1">Username</Label>
                            <Input
                              value={editingConn.username}
                              onChange={e => setEditingConn(ec => ({ ...ec, username: e.target.value }))}
                              className="bg-white/5 border-white/10 text-sm"
                              data-testid={`edit-username-${i}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs mb-1">Password</Label>
                            <Input
                              type="password"
                              value={editingConn.password}
                              onChange={e => setEditingConn(ec => ({ ...ec, password: e.target.value }))}
                              className="bg-white/5 border-white/10 text-sm"
                              data-testid={`edit-password-${i}`}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-[rgb(124,58,237)]" onClick={async () => {
                            try {
                              await updateXtreamConnection(editingConn.server_url, editingConn.username, editingConn.password);
                              toast.success('Credentials updated');
                              setEditingConn(null);
                              const c = await getXtreamConnections();
                              setXtreamConnections(c);
                            } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
                          }}>Save</Button>
                          <Button size="sm" variant="outline" className="border-white/20" onClick={() => setEditingConn(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      /* Display Row */
                      <div className="p-4 flex flex-wrap items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-sm text-white truncate" data-testid={`conn-url-${i}`}>{conn.server_url}</div>
                          <div className="text-xs text-white/50 mt-0.5">{conn.username}</div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-green-400">{(conn.live_count || 0).toLocaleString()} Live</span>
                          <span className="text-blue-400">{(conn.vod_count || 0).toLocaleString()} VOD</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${conn.status === 'complete' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {conn.status || 'pending'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="border-white/20"
                            onClick={() => setEditingConn({ server_url: conn.server_url, username: conn.username || '', password: '' })}
                            data-testid={`edit-conn-${i}`}
                          >
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" className="border-white/20"
                            onClick={async () => {
                              setXtreamImporting(true);
                              try {
                                const res = await importXtream(conn.server_url, conn.username, conn.username, true, true);
                                toast.success(res.message || 'Re-import started');
                              } catch (e) { toast.error('Re-import failed'); }
                              finally { setXtreamImporting(false); }
                            }}
                            data-testid={`reimport-conn-${i}`}
                          >
                            <RefreshCw className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="destructive"
                            onClick={async () => {
                              try {
                                await deleteXtreamConnection(conn.server_url);
                                setXtreamConnections(prev => prev.filter(c => c.server_url !== conn.server_url));
                                toast.success('Connection removed');
                              } catch (e) { toast.error('Failed to remove'); }
                            }}
                            data-testid={`delete-conn-${i}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, sublabel, color }) {
  const colors = {
    primary: 'bg-[rgb(124,58,237)]/20 text-[rgb(124,58,237)]',
    secondary: 'bg-[rgb(16,185,129)]/20 text-[rgb(16,185,129)]',
    accent: 'bg-[rgb(59,130,246)]/20 text-[rgb(59,130,246)]',
    warning: 'bg-[rgb(245,158,11)]/20 text-[rgb(245,158,11)]'
  };

  return (
    <div className="glass-card rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-3xl font-bold">{value}</p>
      <p className="text-sm text-white/60">{label}</p>
      {sublabel && <p className="text-xs text-white/40 mt-1">{sublabel}</p>}
    </div>
  );
}

// Source list component
function SourceList({ sources, color, maxShow }) {
  const colorClass = color === 'green' ? 'text-green-400' : 'text-blue-400';
  const displaySources = maxShow ? sources.slice(0, maxShow) : sources;
  const remaining = maxShow && sources.length > maxShow ? sources.length - maxShow : 0;
  
  return (
    <>
      {displaySources.map((source, i) => (
        <div key={i} className="p-2 bg-white/5 rounded text-sm">
          <span className={`${colorClass} mr-2`}>✓</span>
          {source.name}
        </div>
      ))}
      {remaining > 0 && (
        <div className="p-2 bg-white/5 rounded text-sm text-white/40">
          +{remaining} more...
        </div>
      )}
    </>
  );
}

// Scanner Tab Component
function ScannerTabContent({
  scannerStatus,
  scannerSources,
  scannerLogs,
  isScanning,
  newSourceName,
  newSourceUrl,
  newSourceType,
  setNewSourceName,
  setNewSourceUrl,
  setNewSourceType,
  handleTriggerScan,
  handleAddCustomSource,
  handleRemoveCustomSource
}) {
  const channelCounts = scannerStatus?.channel_counts || {};
  const stats = scannerStatus?.stats || {};
  const iptvOrgSources = scannerSources?.iptv_org || [];
  const publicAggregators = scannerSources?.public_aggregators || [];
  const customSources = scannerSources?.custom || [];
  const totalSources = iptvOrgSources.length + publicAggregators.length + customSources.length;

  return (
    <>
      {/* Scanner Status */}
      <div className="glass-card rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[rgb(16,185,129)]/20">
              <Radar className="w-5 h-5 text-[rgb(16,185,129)]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">Auto Scanner</h3>
              <p className="text-sm text-white/60">Automatically scans for new IPTV content every hour</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {scannerStatus?.is_running ? (
              <span className="flex items-center gap-2 text-sm text-green-400">
                <Wifi className="w-4 h-4" /> Running
              </span>
            ) : (
              <span className="flex items-center gap-2 text-sm text-red-400">
                <WifiOff className="w-4 h-4" /> Stopped
              </span>
            )}
            <Button 
              onClick={handleTriggerScan} 
              disabled={isScanning}
              className="bg-[rgb(124,58,237)] hover:bg-[rgb(124,58,237)]/80"
            >
              {isScanning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Scan Now
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Scanner Stats */}
        {scannerStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-2xl font-bold text-[rgb(124,58,237)]">{channelCounts.total || 0}</p>
              <p className="text-sm text-white/60">Total Channels</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-2xl font-bold text-green-400">{channelCounts.live || 0}</p>
              <p className="text-sm text-white/60">Live TV</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-2xl font-bold text-blue-400">{channelCounts.movies || 0}</p>
              <p className="text-sm text-white/60">Movies</p>
            </div>
            <div className="bg-white/5 rounded-lg p-4">
              <p className="text-2xl font-bold text-yellow-400">{channelCounts.series || 0}</p>
              <p className="text-sm text-white/60">Series</p>
            </div>
          </div>
        )}

        {/* Scan Info */}
        {scannerStatus && (
          <div className="flex flex-wrap gap-4 text-sm text-white/60 mb-6">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Scan Interval: {scannerStatus.scan_interval_hours}h
            </span>
            <span>Auto-cleanup: {scannerStatus.offline_threshold_days} days</span>
            {scannerStatus.last_scan && (
              <span>Last Scan: {new Date(scannerStatus.last_scan).toLocaleString()}</span>
            )}
            <span>Total Scans: {stats.total_scans || 0}</span>
          </div>
        )}
      </div>

      {/* Add Custom Source */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Add Custom Source</h3>
        <div className="flex gap-3 flex-wrap">
          <Input
            placeholder="Source Name"
            value={newSourceName}
            onChange={(e) => setNewSourceName(e.target.value)}
            className="flex-1 min-w-[200px] bg-white/5 border-white/10"
          />
          <Input
            placeholder="M3U/M3U8 URL"
            value={newSourceUrl}
            onChange={(e) => setNewSourceUrl(e.target.value)}
            className="flex-[2] min-w-[300px] bg-white/5 border-white/10"
          />
          <Select value={newSourceType} onValueChange={setNewSourceType}>
            <SelectTrigger className="w-[140px] bg-white/5 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="live">Live TV</SelectItem>
              <SelectItem value="movies">Movies</SelectItem>
              <SelectItem value="series">Series</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleAddCustomSource} className="bg-[rgb(124,58,237)]">
            <Plus className="w-4 h-4 mr-2" />
            Add
          </Button>
        </div>
      </div>

      {/* Sources List */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Scanner Sources ({totalSources} total)</h3>
        
        {/* Custom Sources */}
        {customSources.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-white/60 mb-2">Custom Sources</h4>
            <div className="space-y-2">
              {customSources.map((source) => (
                <div key={source.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 text-xs bg-purple-500/20 text-purple-400 rounded">Custom</span>
                    <span className="font-medium">{source.name}</span>
                    <span className="text-xs text-white/40">{source.type}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveCustomSource(source.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* IPTV-Org Sources */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-white/60 mb-2">IPTV-Org Official ({iptvOrgSources.length})</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <SourceList sources={iptvOrgSources} color="green" maxShow={12} />
          </div>
        </div>

        {/* Public Aggregators */}
        <div>
          <h4 className="text-sm font-medium text-white/60 mb-2">Public Aggregators ({publicAggregators.length})</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <SourceList sources={publicAggregators} color="blue" />
          </div>
        </div>
      </div>

      {/* Recent Scan Logs */}
      <div className="glass-card rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Scan Logs</h3>
        {scannerLogs.length === 0 ? (
          <p className="text-white/60 text-sm">No scan logs yet. Click "Scan Now" to start.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead>Time</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Sources</TableHead>
                <TableHead>Added</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead>Removed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scannerLogs.map((log, i) => (
                <TableRow key={i} className="border-white/10">
                  <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                  <TableCell>{log.duration_seconds ? log.duration_seconds.toFixed(1) : '0'}s</TableCell>
                  <TableCell>{log.sources_scanned}</TableCell>
                  <TableCell className="text-green-400">+{log.channels_added}</TableCell>
                  <TableCell className="text-blue-400">{log.channels_updated}</TableCell>
                  <TableCell className="text-red-400">-{log.channels_removed}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </>
  );
}
